import { bearer } from "@core/utils";
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { type NavigateFunction } from "react-router-dom";
import { z } from "zod";

export type TokenHandlers = {
  getToken: () => Promise<string | undefined>;
  getCcauToken: () => Promise<string | undefined>;
};

const validationErrorSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.literal(400),
  errors: z.record(z.array(z.string())),
  traceId: z.string(),
});

export type ValidationErrorResponse = z.infer<typeof validationErrorSchema>;

export class ValidationError extends Error {
  public readonly validationErrors: Record<string, string[]>;
  public readonly statusCode: number;
  public readonly traceId: string;
  public readonly title: string;

  constructor(response: AxiosResponse<ValidationErrorResponse>) {
    super(response.data.title || "Validation error occurred");
    this.statusCode = response.status;
    this.validationErrors = response.data.errors;
    this.traceId = response.data.traceId;
    this.title = response.data.title;
  }
}

export default class AxiosService {
  private readonly service: AxiosInstance;

  private readonly getToken: () => Promise<string | undefined>;
  private readonly getCcauToken: () => Promise<string | undefined>;

  private readonly navigateTo: NavigateFunction;

  constructor(
    baseUrl: string,
    { getToken, getCcauToken }: TokenHandlers,
    navigateTo: NavigateFunction,
  ) {
    this.service = axios.create({
      baseURL: baseUrl,
      withCredentials: false,
    });

    this.getToken = getToken;
    this.getCcauToken = getCcauToken;
    this.navigateTo = navigateTo;
  }

  private async getConfig(config?: AxiosRequestConfig<unknown>) {
    const token = await this.getToken();
    const ccauToken = await this.getCcauToken();

    const defaultConfig: AxiosRequestConfig = {
      withCredentials: false,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: token ? bearer(token) : undefined,
        "CCAU-Authorization": ccauToken ? bearer(ccauToken) : undefined,
      },
    };

    if (typeof config !== "object") {
      return defaultConfig;
    }

    return Object.assign(defaultConfig, config, {
      headers: {
        ...defaultConfig.headers,
        ...config.headers,
      },
    });
  }

  private isValidationError(data: unknown): data is ValidationErrorResponse {
    return validationErrorSchema.safeParse(data).success;
  }

  protected async get<TResponse, TParams = unknown>(
    url: string,
    config?: AxiosRequestConfig,
    { skipNavigateIfError = false }: { skipNavigateIfError?: boolean } = {},
  ) {
    const cfg = await this.getConfig(config);
    return this.tryCatchHandler(
      () =>
        this.service.get<TResponse, AxiosResponse<TResponse>, TParams>(
          url,
          cfg,
        ),
      { skipNavigateIfError },
    );
  }

  protected async head(
    url: string,
    config?: AxiosRequestConfig,
    { skipNavigateIfError = false }: { skipNavigateIfError?: boolean } = {},
  ) {
    const cfg = await this.getConfig(config);
    return this.tryCatchHandlerForHead(
      () => this.service.head<void>(url, cfg),
      { skipNavigateIfError },
    );
  }

  protected async post<TResponse, TRequest>(
    url: string,
    data: TRequest,
    config?: AxiosRequestConfig,
    { skipNavigateIfError = false }: { skipNavigateIfError?: boolean } = {},
  ) {
    const cfg = await this.getConfig(config);
    return this.tryCatchHandler(
      () =>
        this.service.post<TResponse, AxiosResponse<TResponse>, TRequest>(
          url,
          data,
          cfg,
        ),
      { skipNavigateIfError },
    );
  }

  protected async put<TResponse, TRequest>(
    url: string,
    data: TRequest,
    config?: AxiosRequestConfig,
    { skipNavigateIfError = false }: { skipNavigateIfError?: boolean } = {},
  ) {
    const cfg = await this.getConfig(config);
    return this.tryCatchHandler(
      () =>
        this.service.put<TResponse, AxiosResponse<TResponse>, TRequest>(
          url,
          data,
          cfg,
        ),
      { skipNavigateIfError },
    );
  }

  protected async delete<TResponse>(
    url: string,
    config?: AxiosRequestConfig,
    { skipNavigateIfError = false }: { skipNavigateIfError?: boolean } = {},
  ) {
    const cfg = await this.getConfig(config);
    return this.tryCatchHandler(
      () =>
        this.service.delete<TResponse, AxiosResponse<TResponse>, unknown>(
          url,
          cfg,
        ),
      { skipNavigateIfError },
    );
  }

  protected async patch<TResponse, TRequest>(
    url: string,
    data: TRequest,
    config?: AxiosRequestConfig,
    { skipNavigateIfError = false }: { skipNavigateIfError?: boolean } = {},
  ) {
    const cfg = await this.getConfig(config);
    return this.tryCatchHandler(
      () =>
        this.service.patch<TResponse, AxiosResponse<TResponse>, TRequest>(
          url,
          data,
          cfg,
        ),
      { skipNavigateIfError },
    );
  }

  private async tryCatchHandlerForHead(
    tryCallback: () => Promise<AxiosResponse<void>>,
    { skipNavigateIfError = false }: { skipNavigateIfError?: boolean } = {},
  ) {
    try {
      const response = await tryCallback();
      return this.handleHeadResponse(response, { skipNavigateIfError });
    } catch (err: unknown) {
      if (err instanceof AxiosError && err.response) {
        return this.handleHeadResponse(err.response as AxiosResponse<void>, {
          skipNavigateIfError,
        });
      }

      throw err;
    }
  }

  private handleHeadResponse(
    response: AxiosResponse<void>,
    { skipNavigateIfError = false }: { skipNavigateIfError?: boolean } = {},
  ) {
    this.handleResponseStatusCode(response, { skipNavigateIfError });

    return {
      headers: response.headers,
      status: response.status,
      statusText: response.statusText,
    };
  }

  private async tryCatchHandler<TResponse>(
    tryCallback: () => Promise<AxiosResponse<TResponse>>,
    { skipNavigateIfError = false }: { skipNavigateIfError?: boolean } = {},
  ) {
    try {
      const response = await tryCallback();
      return this.handleResponse(response, { skipNavigateIfError });
    } catch (err: unknown) {
      if (err instanceof AxiosError && err.response) {
        return this.handleResponse(err.response as AxiosResponse<TResponse>, {
          skipNavigateIfError,
          err,
        });
      }

      throw err;
    }
  }

  private handleResponse<TResponse>(
    response: AxiosResponse<TResponse>,
    {
      skipNavigateIfError = false,
      err,
    }: { skipNavigateIfError?: boolean; err?: unknown } = {},
  ) {
    if (skipNavigateIfError) {
      if (this.isValidationError(response.data)) {
        throw new ValidationError(
          response as AxiosResponse<ValidationErrorResponse>,
        );
      }
      if (err) throw err as Error;
    }

    this.handleResponseStatusCode(response, { skipNavigateIfError });
    return response.data;
  }

  private handleResponseStatusCode<TResponse>(
    response: AxiosResponse<TResponse>,
    { skipNavigateIfError = false }: { skipNavigateIfError?: boolean } = {},
  ) {
    if (skipNavigateIfError) {
      return;
    }

    switch (response.status) {
      case 401: {
        this.navigateTo("/");
        break;
      }
      case 403: {
        this.navigateTo("/access-denied");
        break;
      }
      case 404: {
        this.navigateTo("/not-found");
        break;
      }
      default: {
        if (
          (response.status.toString().startsWith("5") ||
            response.status.toString().startsWith("4")) &&
          !response.request.responseURL.includes("assign")
        ) {
          this.navigateTo("/error");
        }
        break;
      }
    }
  }
}
